import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { z } from "zod/v4";
import {
  blockUser,
  getAdminUsers,
  resetUser2Fa,
  unblockUser,
} from "#/modules/users/api.users-admin.server";
import type { UsersAdminSearch } from "#/modules/users/schema.search";
import { adminMiddleware } from "#/modules/util.auth/middleware.admin";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { m } from "#/paraglide/messages";

/** GET /admin/users — paginated, searchable, sortable admin user overview. */
export const getAdminUsersOverview = createServerFn({ method: "GET" })
  .middleware([adminMiddleware])
  .validator((data: UsersAdminSearch) => data)
  .handler(async ({ data }) => {
    const result = await getAdminUsers(data);
    if (Result.isError(result)) throw result.error;

    const totalPages = Math.ceil((result.value?.count || 0) / data.pageSize);
    return {
      users: result.value?.data ?? [],
      totalPages,
      totalCount: result.value?.count || 0,
    };
  });

/** POST — block/unblock a user. Mirrors portal's `api.toggle-user-block`. */
export const toggleUserBlock = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator(z.object({ targetUserId: z.string().min(1), shouldBlock: z.boolean() }))
  .handler(async ({ data }) => {
    const result = data.shouldBlock
      ? await blockUser(data.targetUserId)
      : await unblockUser(data.targetUserId);

    if (Result.isError(result)) {
      throw new Error(m["common.something_went_wrong"]());
    }
  });

/** POST /admin/users/reset-2fa — admin resets a user's 2FA. Mirrors portal's `api.reset-2fa`. */
export const resetUser2FA = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator(z.object({ targetUserId: z.string().min(1) }))
  .handler(async ({ data }) => {
    const result = await resetUser2Fa(data.targetUserId);
    if (Result.isError(result)) {
      throw new Error(m["common.something_went_wrong"]());
    }
  });
